import { useMutation, useQueryClient } from 'react-query'
import * as v from '@badrap/valita'
import { api } from 'src/api'
import { getErrorString } from 'src/utils'
import {
  addOrganization,
  editOrganization,
  createSetupCognitoUser,
  registerNewSubscriber,
  salesforceConnectionTest,
} from './api'
import { keys as OrgKeys } from '../Organization/Client/Info/queries'
import { Region } from 'src/regions'
import { encrypt } from 'src/utils/crypto'
import { toast } from 'sonner'

const keys = {
  all: ['ONBOARIDNG'] as const,
  defaultSettings: () => [...keys.all, 'DEFAULT_SETTINGS'] as const,
  defaultSetting: (orgId: string) => [keys.defaultSettings(), orgId] as const,
}

export function useRegisterNewSubscriber() {
  return useMutation(registerNewSubscriber, {
    onError: (error, params, context) => {
      toast.error(getErrorString(error))
    },
  })
}

export function useCreateSetupCognitoUser() {
  return useMutation(createSetupCognitoUser, {
    onError: (error, params, context) => {
      toast.error(getErrorString(error))
    },
  })
}

export function useCreateOrganization() {
  return useMutation({
    mutationFn: async (params: {
      organization: string
      region: Region
      regToken: string
    }) =>
      api({
        schema: v.object({
          result: v.literal('OK'),
        }),
        method: 'POST',
        url: '/createorganization',
        body: {
          ...params,
          check: encrypt(params.organization),
        },
        withAuth: false,
        region: 'eu-central-1', // always eu-central-1 because we only keep DynamoDB table in that region
      }),
    onError: (error) => {
      toast.error(getErrorString(error))
    },
  })
}

export function useAddOrganization() {
  const queryClient = useQueryClient()

  return useMutation(addOrganization, {
    onSuccess: async (data, params, context) => {
      queryClient.invalidateQueries(OrgKeys.getOrg(params.organization))
    },
    onError: (error, params, context) => {
      toast.error(getErrorString(error))
    },
  })
}

export function useEditOrganization() {
  const queryClient = useQueryClient()

  return useMutation(editOrganization, {
    onSuccess: async (data, params, context) => {
      queryClient.invalidateQueries(OrgKeys.getOrg(params.organization))
      toast.success(`Successfully saved changes.`)
    },
    onError: (error, params, context) => {
      toast.error(getErrorString(error))
    },
  })
}

export function useSalesforceConnectionTest() {
  return useMutation(salesforceConnectionTest, {
    onError: (error, params, context) => {
      toast.error(getErrorString(error))
    },
  })
}
